import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import {
  signIn,
  signOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import type { AuthUser, OrgType, Tier, UserRole } from '../types';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsNewPassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// ── Helpers ─────────────────────────────────────────────────────────

function parseTokenClaims(idToken: Record<string, unknown>): Partial<AuthUser> {
  return {
    sub: idToken['sub'] as string,
    email: idToken['email'] as string,
    name: (idToken['name'] as string) ?? (idToken['email'] as string),
    org_id: (idToken['custom:org_id'] as string) ?? '',
    org_type: ((idToken['custom:org_type'] as string) ?? 'client') as OrgType,
    org_name: (idToken['custom:org_name'] as string) ?? '',
    role: ((idToken['custom:role'] as string) ?? 'viewer') as UserRole,
    tier: ((idToken['custom:tier'] as string) ?? 'base') as Tier,
    features: parseFeatures(idToken['custom:features'] as string),
  };
}

function parseFeatures(raw: string | undefined): string[] {
  if (!raw) return [];
  try {
    return JSON.parse(raw) as string[];
  } catch {
    return raw.split(',').map((f) => f.trim());
  }
}

// ── Provider ────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  // Core session loader — throws on failure so callers can handle it
  const resolveUser = useCallback(async (): Promise<AuthUser> => {
    const cognitoUser = await getCurrentUser();
    const session = await fetchAuthSession({ forceRefresh: true });
    const claims = session.tokens?.idToken?.payload ?? {};

    return {
      ...parseTokenClaims(claims as Record<string, unknown>),
      sub: cognitoUser.userId,
    } as AuthUser;
  }, []);

  // Silent version for initial page load
  const loadUser = useCallback(async () => {
    try {
      setUser(await resolveUser());
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [resolveUser]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      // Clear any stale session before attempting sign-in
      try {
        await signOut();
      } catch {
        // Ignore — no session to clear
      }

      let result;
      try {
        result = await signIn({ username: email, password });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        // Preserve original error message for UI to map to user-friendly copy
        throw new Error(msg);
      }

      if (!result.isSignedIn) {
        if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setNeedsNewPassword(true);
          return;
        }
        throw new Error(
          `Authentication incomplete: ${result.nextStep?.signInStep ?? 'unknown step'}`,
        );
      }

      try {
        const resolved = await resolveUser();
        setUser(resolved);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        throw new Error(`Unable to load your session: ${msg}`);
      }
    },
    [resolveUser],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      try {
        const result = await confirmSignIn({ challengeResponse: newPassword });
        if (!result.isSignedIn) {
          throw new Error('Password reset was not completed. Please try again.');
        }
        setNeedsNewPassword(false);
        const resolved = await resolveUser();
        setUser(resolved);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        throw new Error(msg);
      }
    },
    [resolveUser],
  );

  const logout = useCallback(async () => {
    await signOut();
    setUser(null);
  }, []);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const session = await fetchAuthSession();
    // Use ID token — it contains custom:org_id, custom:org_type etc.
    // Access token only has scopes, not custom Cognito attributes.
    return session.tokens?.idToken?.toString() ?? '';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        needsNewPassword,
        login,
        completeNewPassword,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
